import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Sparkles, Save, Loader2, Languages, Search } from 'lucide-react';
import { EN, LANGUAGES } from '../../i18n/en';

const TARGET_LANGS = LANGUAGES.filter((l) => l.code !== 'en');

const TranslationsManager = () => {
  const { toast } = useToast();
  const [active, setActive] = useState('ms');
  const [strings, setStrings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [filter, setFilter] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');

  const load = async (lang) => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/translations/${lang}`);
      setStrings(res.data?.strings || {});
      setUpdatedAt(res.data?.updated_at || '');
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(active); }, [active]);

  const currentLang = TARGET_LANGS.find((l) => l.code === active) || TARGET_LANGS[0];
  const missingCount = Object.keys(EN).filter((k) => !strings[k]?.trim()).length;

  const filteredKeys = Object.keys(EN).filter((k) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return k.toLowerCase().includes(q) || (EN[k] || '').toLowerCase().includes(q) || (strings[k] || '').toLowerCase().includes(q);
  });

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.post(`/translations/${active}`, { strings });
      toast({ title: 'Saved', description: `${Object.keys(strings).length} strings saved for ${currentLang.label}` });
      load(active);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const autoTranslate = async ({ onlyMissing }) => {
    setTranslating(true);
    try {
      const res = await adminApi.post(`/translations/${active}/auto`, {
        source: EN,
        only_missing: onlyMissing,
      });
      setStrings(res.data?.strings || {});
      toast({
        title: 'AI translation complete',
        description: `Translated ${res.data?.translated || 0} string(s) into ${currentLang.label}.`,
      });
      load(active);
    } catch (e) {
      toast({
        title: 'AI translation failed',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="translations-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Languages className="w-6 h-6 text-[#1565C0]" />
            Translations (UI)
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Multilingual toggle for menus, buttons and section headings across the public site. English is the source-of-truth and lives in the code — edit it there. Other languages are stored per language here.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {TARGET_LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setActive(l.code)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    active === l.code ? 'bg-[#1565C0] text-white border-[#1565C0]' : 'bg-white text-[#0A1628] border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`lang-tab-${l.code}`}
                >
                  <span className="text-base leading-none">{l.flag}</span>
                  {l.native}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded-full ${missingCount === 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {missingCount === 0 ? 'All translated' : `${missingCount} missing`}
              </span>
              {updatedAt && <span className="text-xs text-gray-400">Updated {new Date(updatedAt).toLocaleString()}</span>}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => autoTranslate({ onlyMissing: true })}
              disabled={translating || missingCount === 0}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
              data-testid="ai-fill-missing-btn"
            >
              {translating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              AI fill missing → {currentLang.native}
            </Button>
            <Button
              onClick={() => {
                if (!window.confirm('This will overwrite all existing translations for this language with fresh AI output. Continue?')) return;
                autoTranslate({ onlyMissing: false });
              }}
              variant="outline"
              disabled={translating}
              data-testid="ai-retranslate-all-btn"
            >
              Retranslate all
            </Button>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter by key, English or translation…"
                  className="pl-9"
                  data-testid="translations-filter"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-8 text-center text-gray-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden" data-testid="translations-table">
              <div className="grid grid-cols-12 bg-gray-50 border-b text-xs font-black uppercase tracking-widest text-gray-500 px-3 py-2">
                <div className="col-span-3">Key</div>
                <div className="col-span-4">English (source)</div>
                <div className="col-span-5">{currentLang.native} translation</div>
              </div>
              {filteredKeys.length === 0 && (
                <div className="p-6 text-center text-sm text-gray-500">No keys match &quot;{filter}&quot;</div>
              )}
              {filteredKeys.map((k) => {
                const missing = !strings[k]?.trim();
                return (
                  <div key={k} className={`grid grid-cols-12 items-center px-3 py-2 border-b last:border-b-0 gap-2 ${missing ? 'bg-amber-50/40' : ''}`}>
                    <div className="col-span-3 text-xs text-gray-600 font-mono truncate" title={k}>
                      {k}
                    </div>
                    <div className="col-span-4 text-sm text-gray-700 pr-2">{EN[k]}</div>
                    <div className="col-span-5">
                      <Input
                        value={strings[k] || ''}
                        onChange={(e) => setStrings({ ...strings, [k]: e.target.value })}
                        placeholder={missing ? 'Not translated' : ''}
                        className={missing ? 'border-amber-300' : ''}
                        data-testid={`translation-input-${k}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="sticky bottom-4 z-10 flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#1565C0] shadow-lg"
              data-testid="save-translations-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save {currentLang.native}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TranslationsManager;
